import { admissionDatabaseRequests } from "@/src/api/admissionOfficeDatabaseRequests";
import { officeAccountingPageRequests } from "@/src/api/officeAccountingPageRequests";
import { tasksPageRequests } from "@/src/api/tasksPageRequests";
import { tasksStatusConst } from "@/src/constants/tasksConsts";
import { IApplicantAccountingCreatePayload } from "@/src/types/officeAccounting";
import { matchAuthDocByPdfPath } from "@/src/utils/admissionOffice/matchAuthDocByPdfPath";
import { parseAuthDocsResponse } from "@/src/utils/admissionOffice/parseAuthDocsResponse";
import { formatDateToApiIso } from "@/src/utils/formatDateToApiIso";
import type { FormOfficeApi } from "@/src/utils/tasks/normalizeFormOfficeForApi";

export interface ExecuteDocumentCheckApproveParams {
  fullName: string;
  attachedFile: string;
  taskId: string;
  applicantId: number;
  formOffice: FormOfficeApi | string;
}

export type ExecuteDocumentCheckApproveResult =
  | { ok: true; message: string }
  | { ok: false; message: string };

export const executeDocumentCheckApprove = async ({
  fullName,
  attachedFile,
  taskId,
  applicantId,
  formOffice,
}: ExecuteDocumentCheckApproveParams): Promise<ExecuteDocumentCheckApproveResult> => {
  const authRawResponse = await admissionDatabaseRequests.getAuthDocsByFullname(fullName);
  const authDocs = parseAuthDocsResponse(authRawResponse);
  const matchedDoc = matchAuthDocByPdfPath(authDocs, attachedFile);

  if (!matchedDoc) {
    return {
      ok: false,
      message: "Could not find a matching document in Admissions Office for this PDF.",
    };
  }

  const admissionNo = matchedDoc.document_seria?.trim() ?? "";
  const dataAdmission = formatDateToApiIso(matchedDoc.data_added);
  if (!admissionNo || !dataAdmission) {
    return {
      ok: false,
      message: matchedDoc.data_added?.trim()
        ? "Matched document has an invalid admission date format."
        : "Matched document is missing admission number or admission date.",
    };
  }

  if (!formOffice) {
    return {
      ok: false,
      message: "Could not determine office for this task. Applicant must have MALTA, UKRAINE, or POLAND.",
    };
  }

  const parsedTaskId = Number(taskId);
  if (!Number.isFinite(parsedTaskId) || parsedTaskId <= 0) {
    return {
      ok: false,
      message: "Task ID is invalid for Office Accounting create.",
    };
  }

  const applicantAccountingPayload: IApplicantAccountingCreatePayload = {
    applicant_id: applicantId,
    task_id: parsedTaskId,
    form_office: formOffice as FormOfficeApi,
    admission_no: admissionNo,
    data_admission: dataAdmission,
    general_status: "Active",
  };

  const synced = await officeAccountingPageRequests.createOrUpdateApplicantAccountingForApprove(applicantAccountingPayload);
  if (!synced.ok) {
    return { ok: false, message: synced.message };
  }

  const DONE_STATUS = "done";
  await tasksPageRequests.update(taskId, { status: DONE_STATUS });

  const doneLabel = tasksStatusConst.find((item) => item.id === DONE_STATUS)?.label ?? DONE_STATUS;
  const successMessage =
    synced.mode === "created"
      ? `Office Accounting record created. Task marked as "${doneLabel}".`
      : synced.mode === "updated"
        ? `Office Accounting admission data updated. Task marked as "${doneLabel}".`
        : `Office Accounting record already exists. Task marked as "${doneLabel}".`;

  return { ok: true, message: successMessage };
};
